import {
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

/**
 * Animates a numeric value from its previous value to the new one, writing the
 * formatted result directly to the host element's text content. Useful for
 * balances/counters that update on refresh without a full re-render.
 *
 * The animation runs outside the Angular zone (via `requestAnimationFrame`) so
 * it never triggers change detection on every frame.
 *
 * @example
 * ```html
 * <span [gwCountUp]="wallet.availableBalance" [gwCountUpFormatter]="formatBrl"></span>
 * ```
 */
@Directive({
  selector: '[gwCountUp]',
  standalone: true,
})
export class GwCountUpDirective implements OnChanges, OnDestroy {
  @Input('gwCountUp') value: number | null | undefined = 0;

  /** Animation duration in milliseconds. */
  @Input() gwCountUpDuration = 700;

  /** Optional formatter for the rendered text (e.g. currency). */
  @Input() gwCountUpFormatter?: (value: number) => string;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  private displayedValue = 0;
  private frameId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!('value' in changes)) {
      return;
    }

    const target = this.toFiniteNumber(this.value);

    if (changes['value'].firstChange) {
      // Reveal from zero on first bind for a subtle count-up entrance.
      this.animate(0, target);
      return;
    }

    if (target === this.displayedValue) {
      return;
    }

    this.animate(this.displayedValue, target);
  }

  ngOnDestroy(): void {
    this.cancelFrame();
  }

  private animate(from: number, to: number): void {
    this.cancelFrame();

    if (this.gwCountUpDuration <= 0 || from === to) {
      this.displayedValue = to;
      this.render(to);
      return;
    }

    this.zone.runOutsideAngular(() => {
      const start = performance.now();
      const delta = to - from;

      const step = (now: number): void => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / this.gwCountUpDuration, 1);
        const eased = this.easeOutCubic(progress);
        const current = from + delta * eased;

        this.displayedValue = current;
        this.render(current);

        if (progress < 1) {
          this.frameId = requestAnimationFrame(step);
        } else {
          this.displayedValue = to;
          this.render(to);
          this.frameId = null;
        }
      };

      this.frameId = requestAnimationFrame(step);
    });
  }

  private render(value: number): void {
    const text = this.gwCountUpFormatter
      ? this.gwCountUpFormatter(value)
      : String(value);
    this.el.nativeElement.textContent = text;
  }

  private cancelFrame(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private toFiniteNumber(value: number | null | undefined): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
  }
}
